import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { AuditFormData } from '@/pages/audits/CreateAudit';
import { Zap, Calendar, Repeat } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ScheduleStepProps {
  formData: AuditFormData;
  updateFormData: (updates: Partial<AuditFormData>) => void;
}

const SCHEDULE_TYPES = [
  {
    value: 'immediate' as const,
    label: 'Start Immediately',
    description: 'Begin audit right away',
    icon: Zap,
  },
  {
    value: 'later' as const,
    label: 'Schedule for Later',
    description: 'Set a specific start date',
    icon: Calendar,
  },
  {
    value: 'recurring' as const,
    label: 'Recurring Audit',
    description: 'Repeat on a schedule',
    icon: Repeat,
  },
];

export function ScheduleStep({ formData, updateFormData }: Readonly<ScheduleStepProps>) {
  const today = new Date().toISOString().split('T')[0];
  const defaultDueDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  return (
    <Card className="bg-card border rounded-lg shadow-sm">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Schedule Audit</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Choose when this audit should start and how long it should run.
            </p>
          </div>

      {/* Schedule Type */}
      <div className="space-y-3">
        <Label>
          Schedule Type <span className="text-red-500">*</span>
        </Label>
        <RadioGroup
          value={formData.scheduleType}
          onValueChange={(value: string) => updateFormData({ 
            scheduleType: value as AuditFormData['scheduleType'],
            startDate: value === 'immediate' ? new Date() : undefined,
          })}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {SCHEDULE_TYPES.map((schedule) => {
              const Icon = schedule.icon;
              const isSelected = formData.scheduleType === schedule.value;
              return (
                <label
                  key={schedule.value}
                  className={cn(
                    'flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <RadioGroupItem value={schedule.value} id={schedule.value} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={cn('h-4 w-4', isSelected && 'text-primary')} />
                      <span className="font-medium text-sm">{schedule.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{schedule.description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </RadioGroup>
      </div>

      {/* Start Date (for later and recurring) */}
      {(formData.scheduleType === 'later' || formData.scheduleType === 'recurring') && (
        <div className="space-y-2">
          <Label htmlFor="startDate">
            Start Date <span className="text-red-500">*</span>
          </Label>
          <Input
            id="startDate"
            type="date"
            min={today}
            value={formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : ''}
            onChange={(e) => updateFormData({ startDate: new Date(e.target.value) })}
            required
          />
          <p className="text-xs text-muted-foreground">
            When should this audit begin?
          </p>
        </div>
      )}

      {/* Due Date */}
      <div className="space-y-2">
        <Label htmlFor="dueDate">
          Due Date <span className="text-red-500">*</span>
        </Label>
        <Input
          id="dueDate"
          type="date"
          min={formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : today}
          value={formData.dueDate ? new Date(formData.dueDate).toISOString().split('T')[0] : defaultDueDate}
          onChange={(e) => updateFormData({ dueDate: new Date(e.target.value) })}
          required
        />
        <p className="text-xs text-muted-foreground">
          When should this audit be completed by?
        </p>
      </div>

      {/* Recurrence Settings (for recurring) */}
      {formData.scheduleType === 'recurring' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="recurrencePattern">
              Recurrence Pattern <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.recurrencePattern}
              onValueChange={(value: string) => updateFormData({ 
                recurrencePattern: value as AuditFormData['recurrencePattern'] 
              })}
            >
              <SelectTrigger id="recurrencePattern">
                <SelectValue placeholder="Select pattern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              How often should this audit repeat?
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recurrenceInterval">
              Repeat Every
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="recurrenceInterval"
                type="number"
                min="1"
                max="365"
                value={formData.recurrenceInterval ?? 1}
                onChange={(e) => updateFormData({ recurrenceInterval: parseInt(e.target.value) })}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">
                {formData.recurrencePattern ?? 'period'}(s)
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Optional: Customise the interval (e.g., every 2 weeks)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="recurrenceEndDate">
              End Date (Optional)
            </Label>
            <Input
              id="recurrenceEndDate"
              type="date"
              min={formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : today}
              value={formData.recurrenceEndDate ? new Date(formData.recurrenceEndDate).toISOString().split('T')[0] : ''}
              onChange={(e) => updateFormData({ recurrenceEndDate: e.target.value ? new Date(e.target.value) : undefined })}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty for indefinite recurrence
            </p>
          </div>
        </>
      )}

      {/* Schedule Summary */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-2">
        <p className="text-sm font-medium">Schedule Summary</p>
        <div className="text-sm text-muted-foreground space-y-1">
          {formData.scheduleType === 'immediate' && (
            <p>• Audit will start immediately upon creation</p>
          )}
          {formData.scheduleType === 'later' && formData.startDate && (
            <p>• Audit will start on {new Date(formData.startDate).toLocaleDateString()}</p>
          )}
          {formData.scheduleType === 'recurring' && (
            <>
              <p>• Audit will repeat {formData.recurrencePattern ?? 'periodically'}</p>
              {formData.recurrenceInterval && formData.recurrenceInterval > 1 && (
                <p>• Every {formData.recurrenceInterval} {formData.recurrencePattern}(s)</p>
              )}
              {formData.recurrenceEndDate && (
                <p>• Until {new Date(formData.recurrenceEndDate).toLocaleDateString()}</p>
              )}
            </>
          )}
          {formData.dueDate && (
            <p>• Due date: {new Date(formData.dueDate).toLocaleDateString()}</p>
          )}
        </div>
      </div>
        </div>
      </CardContent>
    </Card>
  );
}
